"use client";

import { useMutation, useQuery } from "@apollo/client";
import { Globe2, Plus } from "lucide-react";
import useTranslation from "next-translate/useTranslation";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { gqlEndorsement, gqlIndustry, gqlUser } from "gql";
import {
  Card,
  LiquidPill,
  LiquidPillExpandButton,
  LiquidPillGroup,
  type LiquidPillGroupItem,
  PageEmptyStateV2,
  PageLoadingStateV2,
  TagPickerV2,
} from "components/ui/v2";
import { forceRefetchQueries } from "settings/apollo";
import { getQueryOperator } from "utils/helpers";
import { ResumeTabHeaderV2 } from "./ResumeTabHeaderV2";

function IndustryLevelSelectorV2({ value, disabled, onChange }: { value: number; disabled?: boolean; onChange: (value: number) => void }) {
  const { t } = useTranslation("common");
  const [rating, setRating] = useState(String(value || 0));

  useEffect(() => setRating(String(value || 0)), [value]);

  const allLevels = [
    { labelKey: "Junior", value: "1", variant: "slate" },
    { labelKey: "Semi-senior", value: "2", variant: "sky" },
    { labelKey: "Senior", value: "3", variant: "indigo" },
    { labelKey: "Expert", value: "4", variant: "emerald" },
  ] satisfies Array<LiquidPillGroupItem & { labelKey: string }>;

  const handleChange = (nextValue: string) => {
    if (disabled) return;
    const next = rating === nextValue && nextValue === "1" ? "0" : nextValue;
    setRating(next);
    onChange(Number(next));
  };

  return (
    <LiquidPillGroup
      items={allLevels.map(({ labelKey, ...level }) => ({ ...level, label: t(labelKey) }))}
      activeValue={rating}
      onChange={handleChange}
      size="sm"
    />
  );
}

function IndustryScoreCardV2({ row, disabled, onChange }: { row: any; disabled?: boolean; onChange: (rating: number, id: string) => void }) {
  const { t } = useTranslation("common");
  return (
    <Card variant="default" padding="comfortable" shadow="subtle" size="full" entranceAnimation>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h3 className="line-clamp-1 text-base font-semibold text-slate-950 dark:text-white">
            {row.industry.name}
          </h3>
          <p className="mt-1 text-sm text-slate-500 dark:text-zinc-400">
            {t("Puntua tu experiencia en este rubro")}
          </p>
        </div>
        <IndustryLevelSelectorV2
          value={row.score || 0}
          disabled={disabled}
          onChange={(rating) => onChange(rating, row.industry.id)}
        />
      </div>
    </Card>
  );
}

export function ResumeIndustriesTabV2() {
  const { t } = useTranslation("common");
  const [industriesList, setIndustriesList] = useState<Array<{ label: string; value: string }>>([]);

  const { data: industriesByUser, loading: userIndustriesLoading } = useQuery(
    gqlUser.queries.GET_INDUSTRIES_SCORE_BY_USER,
    { fetchPolicy: "network-only", notifyOnNetworkStatusChange: true }
  );

  const { data: industries, loading: industriesLoading } = useQuery(
    gqlIndustry.queries.GET_ALL_INDUSTRIES,
    { fetchPolicy: "network-only", notifyOnNetworkStatusChange: true }
  );

  useEffect(() => {
    setIndustriesList(
      industriesByUser?.getUserIndustriesScore.map((row: any) => ({
        label: row.industry.name,
        value: row.industry.id,
      })) || []
    );
  }, [industriesByUser]);

  const [updateProfile, { loading: updateProfileLoading }] = useMutation(
    gqlUser.mutations.UPDATE_USER_PROFILE_INDUSTRIES,
    {
      notifyOnNetworkStatusChange: true,
      refetchQueries: [
        getQueryOperator(gqlUser.queries.GET_FULL_USER),
        getQueryOperator(gqlEndorsement.queries.GET_USER_ENDORSEMENTS_RESULTS),
        getQueryOperator(gqlUser.queries.USER_LEVEL),
        getQueryOperator(gqlUser.queries.GET_INDUSTRIES_SCORE_BY_USER),
      ],
      onCompleted: () => toast.success(t("Perfil actualizado correctamente")),
      onError: (error) => toast.error(error.message),
    }
  );

  const [updateIndustryScore, { loading: loadingUpdateScore }] = useMutation(
    gqlUser.mutations.UPDATE_USER_INDUSTRY_SCORE,
    {
      notifyOnNetworkStatusChange: true,
      refetchQueries: [gqlUser.queries.GET_INDUSTRIES_SCORE_BY_USER],
      awaitRefetchQueries: true,
      onCompleted: () => toast.success(t("Rubro actualizado correctamente")),
      onError: (error) => toast.error(error.message),
    }
  );

  const handleSaveIndustries = async () => {
    await updateProfile({ variables: { industries: industriesList.map((industry) => industry.value) } });
    forceRefetchQueries([gqlUser.queries.GET_FULL_USER]);
  };

  const handleChangeRating = (rating: number, id: string) => {
    updateIndustryScore({ variables: { updateIndustryScoreInput: { id, score: rating } } });
  };

  const scoreRows = industriesByUser?.getUserIndustriesScore || [];
  const options = industries?.getAllIndustries.map((row: any) => ({ value: row.id, label: row.name })) || [];

  return (
    <div className="grid gap-4">
      <ResumeTabHeaderV2
        title={t("Rubros")}
        description={t("Revisa y agrega los rubros en los cuales has trabajado")}
        action={
          <LiquidPillExpandButton
            icon={<Plus className="h-4 w-4" />}
            label={updateProfileLoading ? t("Guardando") : t("Agregar rubro")}
            variant="sky"
            size="sm"
            expandTo="left"
            disabled={updateProfileLoading}
            onClick={handleSaveIndustries}
            aria-label={t("Agregar rubro")}
          />
        }
      />

      <Card variant="default" padding="comfortable" shadow="subtle" size="full">
        <TagPickerV2
          data={options}
          value={industriesList.map((row) => row.value)}
          onChange={(values) => {
            setIndustriesList(values.map((value) => ({
              value,
              label: options.find((option: any) => option.value === value)?.label || value,
            })));
          }}
          creatable={false}
          placeholder={t("SELECT_INDUSTRY_PLACEHOLDER")}
          size="md"
          variant="sky"
          loading={industriesLoading}
        />
      </Card>

      {userIndustriesLoading ? (
        <PageLoadingStateV2 title={t("Cargando")} description={t("Rubros")} />
      ) : scoreRows.length === 0 ? (
        <PageEmptyStateV2
          variant="empty"
          icon={<Globe2 className="h-8 w-8" />}
          title={t("Aún no tienes rubros cargados")}
          description={t("Agrega rubros para indicar tus industrias y áreas de experiencia")}
        />
      ) : (
        <div className="grid gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <LiquidPill as="span" variant="sky" appearance="tinted" shadow="neutral" size="xs" text={t("Mis rubros")} />
            <LiquidPill as="span" variant="slate" appearance="tinted" shadow="neutral" size="xs" text={t("Puntua tu experiencia en cada rubro")} />
          </div>
          {scoreRows.map((row: any) => (
            <IndustryScoreCardV2
              key={row.industry.id}
              row={row}
              disabled={loadingUpdateScore}
              onChange={handleChangeRating}
            />
          ))}
        </div>
      )}
    </div>
  );
}
